import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { FormModal, ModalFooter } from 'pages/global/FormModal';
import { Modal } from 'react-bootstrap';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useGetTableTemplate } from 'hooks/useGetTableTemplates';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { getColumns, getTableDataSet } from 'helpers/TableHelper';
import { RenderTable } from 'pages/documents_v2/views/edit_area/blocks/block_types/table/RenderTable';
import _ from 'lodash';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';

export function EditTableModal({ block, hide, packageGroups, document, index }) {
    const { t } = useTranslation();
    const table = getTableData();
    const template = useGetTableTemplate(parseInt(table.templateId));
    const { updateBlock } = useContext(EditAreaContext);

    if (template === undefined) {
        return null;
    }

    return (
        <FormModal
            show={true}
            size="lg"
            scrollable={true}
            onHide={hide}
            title={t('documents:documents.tables.editTable', { name: template.name })}
        >
            <>
                <div className="modal-editor-toolbar" id="modal-editor-toolbar">
                    <div id="editor-toolbar-placeholder"></div>
                </div>

                <Modal.Body>
                    <TableForm
                        table={table}
                        template={template}
                        packageGroups={packageGroups}
                        document={document}
                        handleFormSubmit={handleFormSubmit}
                    />
                </Modal.Body>
                <ModalFooter form="edit-table-form" onHide={hide} />
            </>
        </FormModal>
    );

    function handleFormSubmit(values) {
        updateBlock(JSON.stringify(values), index);
        hide();
    }

    function getTableData() {
        const blockContent = block.latestContent || block.baseContent || '';

        return HelperFunctions.tryParseJSON(blockContent, {
            data: [],
            templateId: false,
            title: '',
            properties: {},
        });
    }
}

function TableForm({ table, template, packageGroups, document, handleFormSubmit }) {
    const { t } = useTranslation();
    const [packageGroupId, setPackageGroupId] = useState();
    const { activeVariantId } = useContext(AreaContext);

    const [formData, setFormData] = useState({
        ...table,
        properties: {
            ...table.properties,
            ...template.properties,
        },
    });

    const differentCellValuePerVariant = _.get(template, 'properties.differentCellValuePerVariant', false);

    const columns = getColumns(template, packageGroupId, activeVariantId, document);
    const tableDataSet = getTableDataSet(formData, activeVariantId) ?? {
        rows: [],
        variantId: activeVariantId,
        packageGroupId,
    };

    useEffect(() => {
        if (tableDataSet?.packageGroupId && !packageGroupId) {
            setPackageGroupId(tableDataSet.packageGroupId);
        }
    }, [tableDataSet]);

    return (
        <form id="edit-table-form" onSubmit={onSubmit}>
            <div className="dr-side-container form-container form">
                <div className="mb-3">
                    <label className="mb-2 font-weight-bolder label-title" htmlFor="title">
                        {t('documents:documents.tables.tableTitle')}
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        name="title"
                        id="title"
                        value={formData.title}
                        onChange={handleTitleChange}
                    />
                </div>

                {template.columns === 'packages' && (
                    <>
                        <div className="mb-3">
                            <label className="mb-2 font-weight-bolder label-title" htmlFor="packageGroupId">
                                {t('documents:documents.tables.packageGroup')}
                            </label>
                            <SelectPackageGroup
                                packageGroups={packageGroups}
                                packageGroupId={packageGroupId}
                                selectPackageGroup={setPackageGroupId}
                            />
                        </div>
                    </>
                )}

                {differentCellValuePerVariant && (
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.properties.differentCellValuePerVariant}
                            onChange={() => {
                                handlePropertyChange(
                                    'differentCellValuePerVariant',
                                    !formData.properties.differentCellValuePerVariant,
                                );
                            }}
                            id="differentCellValuePerVariant"
                        />
                        <label className="form-check-label" htmlFor="differentCellValuePerVariant">
                            {t('documents:documents.tables.differentCellValuePerVariant')}
                        </label>
                    </div>
                )}
            </div>

            {columns && (
                <div className="pt-3">
                    <RenderTable
                        columns={columns}
                        enableTableEditor={true}
                        handleCellChange={handleCellChange}
                        modal={true}
                        tableData={tableDataSet?.rows ?? []} // Override tableData in modal with the formData
                        template={template}
                        rows={template.rows}
                    />
                </div>
            )}
        </form>
    );

    function onSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        handleFormSubmit(formData);
    }

    function handleCellChange(rowId, entityId, value) {
        let newDataSetRows = [...(tableDataSet?.rows ?? [])];

        // Check if dataset has cells of rowId
        if (!newDataSetRows.some((_row) => _row.id === rowId)) {
            newDataSetRows.push({
                id: rowId,
                cells: [],
            });
        }

        newDataSetRows = newDataSetRows.map((row) => {
            if (row.id === rowId && row.hasOwnProperty('cells')) {
                let found = false;

                row.cells = row.cells.map((cell) => {
                    if (cell.entityId === entityId) {
                        found = true;
                        cell.latestContent = value;
                    }
                    return cell;
                });

                // Cell not found, add it
                if (!found) {
                    row.cells.push({
                        entityId: entityId,
                        latestContent: value,
                        diffContent: '',
                    });
                }
            }

            return row;
        });

        const newTable = handleTableDataChange(
            { properties: formData.properties, rows: newDataSetRows },
            tableDataSet,
            packageGroupId,
            formData,
            activeVariantId,
        );

        setFormData(newTable);
    }

    function handlePropertyChange(propName, newValue) {
        setFormData({
            ...formData,
            properties: {
                ...formData.properties,
                [propName]: newValue,
            },
        });
    }

    function handleTitleChange(e) {
        setFormData({
            ...formData,
            title: e.target.value,
        });
    }
}

function SelectPackageGroup({ packageGroupId, packageGroups = [], selectPackageGroup }) {
    const { t } = useTranslation();

    return (
        <select
            name="packageGroupId"
            className="custom-select mr-2"
            value={packageGroupId}
            onChange={(e) => selectPackageGroup(parseInt(e.target.value))}
        >
            <option value="">{t('documents:documents.tables.selectPackageGroup')}</option>
            {packageGroups.map((packageGroup) => {
                return (
                    <option value={packageGroup.id} key={packageGroup.id}>
                        {packageGroup.name}
                    </option>
                );
            })}
        </select>
    );
}

function handleTableDataChange(formData, currentDataSet, packageGroupId, table, activeVariantId) {
    const { properties, rows } = formData;
    const dataSet = {
        ...currentDataSet,
        packageGroupId,
        rows,
    };
    let newData = [];
    let datasetFound = false;

    if (table.data !== undefined && table.data.length > 0) {
        // merge with existing data
        newData = table.data.map((dataItem) => {
            if (dataItem.variantId === activeVariantId) {
                datasetFound = true;
                return dataSet;
            } else {
                return dataItem;
            }
        });
    } else {
        // create array with the current dataSet as only item
        newData.push(dataSet);
        datasetFound = true;
    }

    if (!datasetFound) {
        newData.push(dataSet);
    }

    return {
        ...table,
        data: newData,
        properties,
    };
}
